import React, { useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const DocumentAttachment = ({ documents = [], onChange, entityType = 'property', entityId = '' }) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    const newDocuments = files?.map((file, index) => ({
      id: Date.now() + index,
      name: file?.name,
      size: file?.size,
      type: getFileType(file?.name),
      date: new Date()?.toISOString()?.split('T')?.[0],
      url: URL.createObjectURL(file),
      category: 'عام',
      uploadedBy: 'المستخدم الحالي'
    }));

    onChange([...(documents || []), ...newDocuments]);
  };

  const handleRemoveDocument = (docId) => {
    const updatedDocuments = documents?.filter(doc => doc?.id !== docId);
    onChange(updatedDocuments);
  };

  const handleDownloadDocument = (doc) => {
    const link = document.createElement('a');
    link.href = doc?.url;
    link.download = doc?.name;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const handleCategoryChange = (docId, newCategory) => {
    const updatedDocuments = documents?.map(doc => 
      doc?.id === docId ? { ...doc, category: newCategory } : doc
    );
    onChange(updatedDocuments);
  };

  const getFileType = (filename) => {
    const extension = filename?.split('.')?.pop()?.toLowerCase();
    if (['pdf']?.includes(extension)) return 'pdf';
    if (['doc', 'docx']?.includes(extension)) return 'word';
    if (['xls', 'xlsx']?.includes(extension)) return 'excel';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp']?.includes(extension)) return 'image';
    if (['zip', 'rar']?.includes(extension)) return 'archive';
    return 'other';
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
        return 'FileText';
      case 'word':
        return 'FileText';
      case 'excel':
        return 'FileSpreadsheet';
      case 'image':
        return 'Image';
      case 'archive':
        return 'Archive';
      default:
        return 'File';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'عقود':
        return 'bg-primary/10 text-primary';
      case 'وثائق':
        return 'bg-secondary/10 text-secondary';
      case 'تقارير':
        return 'bg-success/10 text-success';
      case 'مالية':
        return 'bg-warning/10 text-warning';
      case 'صور':
        return 'bg-info/10 text-info';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' بايت';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' كيلوبايت';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' ميجابايت';
  };

  const categories = ['عام', 'عقود', 'وثائق', 'تقارير', 'مالية', 'صور', 'أخرى'];

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-border rounded-lg p-6 text-center bg-muted/30 hover:bg-muted/50 transition-all duration-250">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />

        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="Upload" size={24} className="text-primary" />
          </div>

          <div>
            <h3 className="text-base font-heading font-semibold text-foreground mb-1">
              رفع المستندات والملفات
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              اسحب وأفلت الملفات هنا أو انقر للاختيار
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => fileInputRef?.current?.click()}
            iconName="FilePlus"
            iconPosition="left"
            size="sm"
          >
            اختيار الملفات
          </Button>

          <p className="text-xs text-muted-foreground">
            PDF, Word, Excel, الصور، والملفات المضغوطة (حد أقصى 10 ميجابايت لكل ملف)
          </p>
        </div>
      </div>

      {documents && documents?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-heading font-semibold text-foreground">
              المستندات المرفقة ({documents?.length})
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange([])}
              iconName="Trash2"
              iconPosition="left"
            >
              حذف الكل
            </Button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {documents?.map((doc) => (
              <div
                key={doc?.id}
                className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-all duration-250"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon
                      name={getFileIcon(doc?.type)}
                      size={20}
                      className="text-primary"
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground line-clamp-1 mb-1">
                      {doc?.name}
                    </h4>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-2">
                      <span className="font-data">{formatFileSize(doc?.size)}</span>
                      <span className="font-data">{doc?.date}</span>
                    </div>
                    <select
                      value={doc?.category}
                      onChange={(e) => handleCategoryChange(doc?.id, e?.target?.value)}
                      className={`text-xs px-2 py-1 rounded-full font-caption border-0 ${getCategoryColor(doc?.category)}`}
                    >
                      {categories?.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName="Download"
                      iconSize={16}
                      onClick={() => handleDownloadDocument(doc)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName="Trash2"
                      iconSize={16}
                      onClick={() => handleRemoveDocument(doc?.id)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentAttachment;